import { useSpecialQualifyingStatuses } from '@iheartjane/data-access/react-query';
import { getApplicableStoreSpecialIds, getSpecialType } from '@iheartjane/menu';
import { Box, Flex, Link, TagFilledIcon, Typography } from '@iheartjane/reefer';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useKioskSpacing } from '@jane/kiosk/hooks';
import { isSupportedStoreSpecial } from '@jane/kiosk/util';
import { trackStoreSpecialPromptTap } from '@jane/shared-ecomm/tracking';

import { SpecialPdpCallout } from '../lib/SpecialPdpCallout/SpecialPdpCallout';
import type { SpecialPdpCalloutContainerProps } from './StoreSpecialPdpCallout.types';

const LABEL_ID = 'button-label';
export const StoreSpecialPdpCallout = ({
  cart,
  product,
  specials,
  store,
}: SpecialPdpCalloutContainerProps) => {
  const [viewAll, setViewAll] = useState(false);
  const { getKioskSpacing } = useKioskSpacing();
  const navigate = useNavigate();

  const qualifyingSpecials = useMemo(() => {
    return specials.filter(
      (special) =>
        isSupportedStoreSpecial(special, true) &&
        getApplicableStoreSpecialIds(product).has(special.id)
    );
  }, [product, specials]);

  const qualifyingStatuses = useSpecialQualifyingStatuses({
    cartUuid: cart?.uuid || '0',
    cartProducts: cart?.products || [],
    specials: qualifyingSpecials,
    storeId: store.id,
  });

  const specialCalloutItems = useMemo(() => {
    return qualifyingSpecials.map((special) => ({
      id: special.id,
      title: special.title,
      special_type: special.special_type,
      isApplied:
        special.special_type === 'product'
          ? true
          : qualifyingStatuses[special.id]?.qualifying_status === 'applied',
      isQualified:
        qualifyingStatuses[special.id]?.qualifying_status === 'qualified',
    }));
  }, [qualifyingSpecials, qualifyingStatuses]);

  const displayedSpecials = useMemo(() => {
    return specialCalloutItems.slice(0, viewAll ? undefined : 3);
  }, [specialCalloutItems, viewAll]);

  const onClick = (specialId: number) => {
    const special = qualifyingSpecials.find((s) => s.id === specialId);
    if (special) {
      trackStoreSpecialPromptTap({
        special,
        cityState: `${store.city}, ${store.state}`,
        source: 'pdp',
        storeFulfillmentType: 'pickup',
        storeSearchRadius: 0,
        storeType: 'all',
        storeId: store.id,
        storeName: store.name,
        storeCity: store.city || '',
        storeState: store.state,
      });
    }
    navigate(`/stores/${store.id}/menu/special/${specialId}`);
  };

  if (!specialCalloutItems.length) {
    return null;
  }

  return (
    <Box mb={24}>
      <Flex flexDirection="row" alignContent="center" gap={8}>
        <TagFilledIcon color="info" size="md" />
        <Typography mb={getKioskSpacing(24, 16)} variant="body-bold">
          {(store?.store_compliance_language_settings &&
            store?.store_compliance_language_settings['specials']) ||
            'Deals'}
        </Typography>
      </Flex>
      <Flex flexDirection="column" gap={16}>
        {displayedSpecials.map((special) => (
          <SpecialPdpCallout
            key={special.id}
            showButton={!special.isApplied}
            showCheck={special.isApplied}
            title={special.title}
            specialTypeLabel={getSpecialType(
              special.special_type,
              store.store_compliance_language_settings
            )}
            onClick={() => onClick(special.id)}
          />
        ))}
        {specialCalloutItems.length > 3 && (
          <Flex
            justifyContent="center"
            aria-labelledby={LABEL_ID}
            py={8}
            onClick={() => setViewAll(!viewAll)}
          >
            <Link
              typography="section"
              color="primary"
              id={LABEL_ID}
            >{`View ${viewAll ? 'less' : 'all'}`}</Link>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
